import { MapPinTypeVariant, MapPinVariant } from '../MapPin/types'

// ─── Pin dimensions (must stay in sync with MapPinNew.tsx) ───────────────────
export const PIN_W = 27
export const PIN_H = 56
export const PIN_ANCHOR_X = PIN_W / 2 // 13.5 — horizontal centre
export const PIN_ANCHOR_Y = PIN_H // 56   — bottom tip

// Knock-status badge dimensions (matches MapPinNew: width=35, height=35)
export const KS_W = 35
export const KS_H = 35
// Badge SVG uses viewBox 50x50 and the pointer tip sits at (25.5651, 42.6109).
// Use the actual pointer tip as anchor so coordinates map to the visual tip.
const KS_VIEWBOX_SIZE = 50
export const KS_TIP_X = (25.5651 / KS_VIEWBOX_SIZE) * KS_W
export const KS_TIP_Y = (42.6109 / KS_VIEWBOX_SIZE) * KS_H
export const KS_ANCHOR_X = KS_TIP_X
export const KS_ANCHOR_Y = KS_TIP_Y

// ─── Colour helpers ──────────────────────────────────────────────────────────
export function getPinBgColor(
  variant: MapPinVariant,
  selected: boolean,
  multitenant: boolean,
  isLost: boolean = false,
): string {
  if (selected) return '#3E98FF'
  // SH-1326: DoNotKnock keeps its red on multi-record pins.
  if (multitenant && variant !== MapPinTypeVariant.DoNotKnock) return '#A1C1E6'
  if (isLost) return '#D63B1F'
  switch (variant) {
    case MapPinTypeVariant.PestPac:
      return '#002D5C'
    case MapPinTypeVariant.PendingLead:
    case MapPinTypeVariant.DraftLead:
      return '#F9BA1B'
    case MapPinTypeVariant.Opportunity:
      return '#31805B'
    case MapPinTypeVariant.Lead:
      return '#1565C0'
    case MapPinTypeVariant.DoNotKnock:
      return '#D63B1F'
    default:
      return '#4b2d87'
  }
}

export function getPinIconColor(
  variant: MapPinVariant,
  multitenant: boolean,
): string {
  // SH-1326: navy is unreadable on DoNotKnock's red body.
  const grouped = multitenant || variant === MapPinTypeVariant.Mixed
  if (grouped && variant !== MapPinTypeVariant.DoNotKnock) return '#19305A'
  return 'white'
}

// ─── Icon content strings (paths only, no wrapping <svg>) ────────────────────
// Each returns { vb: viewBox, content: SVG path/shape elements as string }

type IconContent = { vb: string; content: string }

function leadIcon(color: string): IconContent {
  return {
    vb: '0 0 10 10',
    content:
      `<path d="M5 5C6.38125 5 7.5 3.88125 7.5 2.5C7.5 1.11875 6.38125 0 5 0C3.61875 0 2.5 1.11875 2.5 2.5C2.5 3.88125 3.61875 5 5 5ZM5 6.25C3.33125 6.25 0 7.0875 0 8.75V10H10V8.75C10 7.0875 6.66875 6.25 5 6.25Z" fill="${color}"/>`.replace(
        '${color}',
        color,
      ),
  }
}

function draftLeadIcon(color: string): IconContent {
  return {
    vb: '0 0 16 11',
    content:
      `<path d="M9.625 5.5C11.1444 5.5 12.375 4.26938 12.375 2.75C12.375 1.23062 11.1444 0 9.625 0C8.10562 0 6.875 1.23062 6.875 2.75C6.875 4.26938 8.10562 5.5 9.625 5.5ZM3.4375 4.125V2.0625H2.0625V4.125H0V5.5H2.0625V7.5625H3.4375V5.5H5.5V4.125H3.4375ZM9.625 6.875C7.78937 6.875 4.125 7.79625 4.125 9.625V11H15.125V9.625C15.125 7.79625 11.4606 6.875 9.625 6.875Z" fill="${color}"/>`.replace(
        '${color}',
        color,
      ),
  }
}

function opportunityIcon(color: string): IconContent {
  return {
    vb: '0 0 7 13',
    content:
      `<path d="M3.7675 5.43125C2.20688 5.02562 1.705 4.60625 1.705 3.95312C1.705 3.20375 2.39937 2.68125 3.56125 2.68125C4.785 2.68125 5.23875 3.26562 5.28 4.125H6.79937C6.75125 2.9425 6.02937 1.85625 4.5925 1.50563V0H2.53V1.485C1.19625 1.77375 0.12375 2.64 0.12375 3.96688C0.12375 5.555 1.43687 6.34562 3.355 6.80625C5.07375 7.21875 5.4175 7.82375 5.4175 8.46313C5.4175 8.9375 5.08062 9.69375 3.56125 9.69375C2.145 9.69375 1.58813 9.06125 1.5125 8.25H0C0.0825 9.75563 1.21 10.6013 2.53 10.8831V12.375H4.5925V10.8969C5.93313 10.6425 6.99875 9.86563 6.99875 8.45625C6.99875 6.50375 5.32812 5.83687 3.7675 5.43125Z" fill="${color}"/>`.replace(
        '${color}',
        color,
      ),
  }
}

function pestPacIcon(color: string): IconContent {
  return {
    vb: '0 0 19 19',
    content:
      `<path d="M9.28906 0.25C14.2809 0.250182 18.3271 4.29717 18.3271 9.28906C18.327 14.2809 14.2808 18.3279 9.28906 18.3281C4.29719 18.3281 0.250123 14.281 0.25 9.28906C0.25 4.29705 4.29711 0.25 9.28906 0.25Z" fill="#19305A" stroke="white" stroke-width="0.5"/>` +
      `<path d="M17.8637 11.7977H17.2102V10.3447L18.1886 10.058C18.2095 9.80746 18.2213 9.55334 18.2203 9.29741C18.2194 8.91261 18.194 8.53324 18.145 8.16114L17.2075 8.43523C17.183 6.90961 16.349 5.57277 15.1174 4.83674L15.3234 4.30309C15.3806 4.15425 15.4913 4.02901 15.6347 3.95096L16.162 3.66508C15.9823 3.45089 15.7917 3.24578 15.5921 3.04975L14.9586 3.39281C14.8152 3.47086 14.7045 3.59519 14.6473 3.74494L14.3633 4.48098C13.9167 4.32124 13.4366 4.23412 12.9375 4.23412H12.7877C12.3594 4.23412 11.9455 4.29856 11.5544 4.41745L11.2957 3.79214C11.2349 3.6442 11.1215 3.52168 10.9754 3.44726L8.7564 2.30918C8.55674 2.20662 8.29899 2.26017 8.18283 2.44713C8.0594 2.64679 8.13473 2.90272 8.34528 3.01072L10.3138 4.02084C10.459 4.09526 10.5724 4.21778 10.6341 4.36572L10.7875 4.736C9.46247 5.4439 8.55039 6.82975 8.51953 8.42252L7.62559 8.16114C7.30976 8.06857 7.03296 7.87345 6.84691 7.61116L5.6562 5.93761C5.37214 5.53919 4.82307 5.40215 4.40469 5.62723C4.16873 5.75429 3.99902 5.9721 3.94002 6.22622C3.88194 6.47489 3.93458 6.73808 4.08342 6.94773L5.63352 9.12588C5.84407 9.42084 6.15445 9.64047 6.5093 9.74484L8.51862 10.3329V11.7987H4.85574C4.29397 11.7987 3.83838 12.2388 3.83838 12.7815C3.83838 13.3243 4.29397 13.7644 4.85574 13.7644H8.51953V14.9715C8.51953 15.0169 8.52134 15.0623 8.52316 15.1076L6.51021 15.6966C6.15536 15.8001 5.84497 16.0206 5.63442 16.3156L5.16068 17.1566C5.71519 17.467 6.30692 17.7184 6.92768 17.9035C7.10919 17.6921 7.35332 17.3584 7.62741 17.2785L8.97603 16.8837C9.20382 17.3348 9.50876 17.7405 9.8736 18.0826C10.7503 18.04 11.5934 17.8684 12.3848 17.5871V9.42447H9.45884V8.32723C9.45884 7.13651 10.1223 6.08918 11.1097 5.51106L11.5625 6.60649C11.5816 6.65187 11.6079 6.69271 11.6397 6.72719C11.7613 6.85788 11.9655 6.90326 12.1416 6.81614C12.3367 6.71993 12.4111 6.48669 12.3303 6.29066L11.872 5.18161C12.186 5.08814 12.52 5.03822 12.8649 5.03822C13.2878 5.03822 13.6935 5.11445 14.0683 5.2524L13.6771 6.26615C13.6009 6.464 13.6817 6.69634 13.8786 6.788C14.0565 6.87059 14.2589 6.82158 14.3778 6.68817C14.4086 6.65277 14.434 6.61193 14.4522 6.56656L14.8116 5.6354C15.6919 6.23166 16.27 7.21819 16.27 8.32904V9.42629H13.345V17.1796C15.5122 16.1014 17.1694 14.1556 17.8637 11.7977Z" fill="${color}"/>` +
      `<path d="M13.3402 9.43104H16.2653V8.33379C16.2653 7.22293 15.6863 6.23641 14.8068 5.64015L14.4474 6.5713C14.4302 6.61759 14.4048 6.65843 14.373 6.69292C14.2541 6.82633 14.0518 6.87625 13.8739 6.79275C13.6769 6.70109 13.5962 6.46966 13.6724 6.2709L14.0636 5.25715C13.6887 5.1192 13.2831 5.04297 12.8601 5.04297C12.5153 5.04297 12.1822 5.09379 11.8673 5.18636L12.3256 6.29541C12.4064 6.49144 12.331 6.72559 12.1368 6.82088C11.9608 6.90801 11.7575 6.86263 11.6349 6.73194C11.6032 6.69746 11.5769 6.65662 11.5578 6.61124L11.1049 5.51581C10.1175 6.09393 9.4541 7.14125 9.4541 8.33198V9.42922H12.3791V17.5918C12.7095 17.4748 13.0299 17.3377 13.3411 17.1834L13.3402 9.43104Z" fill="#19305A"/>`,
  }
}

function groupIcon(color: string): IconContent {
  return {
    vb: '0 0 24 24',
    content:
      `<path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="${color}"/>`.replace(
        '${color}',
        color,
      ),
  }
}

// Position of the icon within the 27×56 pin SVG.
// The icon sits in the upper 60% (0–33.6px), centred at x=13.5.
type IconPlacement = { x: number; y: number; w: number; h: number }

function getIconPlacement(
  variant: MapPinVariant,
  isGroup: boolean,
): IconPlacement {
  if (isGroup) return { x: 6.5, y: 9, w: 14, h: 14 }
  switch (variant) {
    case MapPinTypeVariant.PestPac:
      return { x: 4.75, y: 11.75, w: 16.87, h: 16.87 }
    case MapPinTypeVariant.Opportunity:
      return { x: 10, y: 15, w: 7, h: 13 }
    case MapPinTypeVariant.PendingLead:
    case MapPinTypeVariant.DraftLead:
      return { x: 7, y: 13, w: 13, h: 9 }
    default:
      return { x: 8.5, y: 12, w: 10, h: 10 }
  }
}

function getIconContent(
  variant: MapPinVariant,
  isGroup: boolean,
  color: string,
): IconContent {
  if (isGroup) return groupIcon(color)
  switch (variant) {
    case MapPinTypeVariant.PestPac:
      return pestPacIcon(color)
    case MapPinTypeVariant.Opportunity:
      return opportunityIcon(color)
    case MapPinTypeVariant.PendingLead:
    case MapPinTypeVariant.DraftLead:
      return draftLeadIcon(color)
    default:
      return leadIcon(color)
  }
}

// ─── Main pin SVG ─────────────────────────────────────────────────────────────
export function pinBodySvg(
  variant: MapPinVariant,
  bgColor: string,
  iconColor: string,
  isGroup: boolean,
): string {
  const icon = getIconContent(variant, isGroup, iconColor)
  const p = getIconPlacement(variant, isGroup)
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_W}" height="${PIN_H}" viewBox="0 0 ${PIN_W} ${PIN_H}">`,
    `<defs><filter id="ds" x="-50%" y="-50%" width="200%" height="200%">`,
    `<feDropShadow dx="0" dy="4" stdDeviation="3" flood-color="rgba(0,0,0,0.2)"/></filter></defs>`,
    `<path d="M13.4385 0.5L25.667 16.6777C26.1553 17.3238 26.3007 18.1663 26.0566 18.9385L14.5 55.5H12.9473L0.958008 18.9277C0.705836 18.1584 0.84109 17.3142 1.32227 16.6631L13.2666 0.5H13.4385Z"`,
    ` fill="${bgColor}" stroke="white" stroke-width="2" stroke-linejoin="round" filter="url(#ds)"/>`,
    `<svg x="${p.x}" y="${p.y}" width="${p.w}" height="${p.h}" viewBox="${icon.vb}" overflow="visible">`,
    icon.content,
    `</svg>`,
    `</svg>`,
  ].join('')
}

// ─── Knock-status badge SVG ───────────────────────────────────────────────────
// Icon paths keyed by IKnockStatusIcon numeric value (0–20)
function ksIconContent(
  icon: number | null,
  color: string,
): { vb: string; paths: string } {
  const c = color
  switch (icon) {
    case 0:
      return {
        vb: '0 0 20 20',
        paths: `<path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.58 18 2 14.42 2 10C2 8.15 2.63 6.45 3.69 5.1L14.9 16.31C13.55 17.37 11.85 18 10 18ZM16.31 14.9L5.1 3.69C6.45 2.63 8.15 2 10 2C14.42 2 18 5.58 18 10C18 11.85 17.37 13.55 16.31 14.9Z" fill="${c}"/>`,
      }
    case 1:
      return {
        vb: '0 0 20 19',
        paths: `<path d="M20 7.24L12.81 6.62L10 0L7.19 6.63L0 7.24L5.46 11.97L3.82 19L10 15.27L16.18 19L14.55 11.97L20 7.24ZM10 13.4L6.24 15.67L7.24 11.39L3.92 8.51L8.3 8.13L10 4.1L11.71 8.14L16.09 8.52L12.77 11.4L13.77 15.68L10 13.4Z" fill="${c}"/>`,
      }
    case 2:
      return {
        vb: '0 -1.5 20 19',
        paths: `<path d="M14.5 0C12.76 0 11.09 0.81 10 2.09C8.91 0.81 7.24 0 5.5 0C2.42 0 0 2.42 0 5.5C0 9.28 3.4 12.36 8.55 17.04L10 18.35L11.45 17.03C16.6 12.36 20 9.28 20 5.5C20 2.42 17.58 0 14.5 0ZM10.1 15.55L10 15.65L9.9 15.55C5.14 11.24 2 8.39 2 5.5C2 3.5 3.5 2 5.5 2C7.04 2 8.54 2.99 9.07 4.36H10.94C11.46 2.99 12.96 2 14.5 2C16.5 2 18 3.5 18 5.5C18 8.39 14.86 11.24 10.1 15.55Z" fill="${c}"/>`,
      }
    case 3:
      return {
        vb: '0 0 20 20',
        paths: `<path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM10 18C5.58 18 2 14.42 2 10C2 5.58 5.58 2 10 2C14.42 2 18 5.58 18 10C18 14.42 14.42 18 10 18Z" fill="${c}"/>`,
      }
    case 4:
      return {
        vb: '0 0 14 14',
        paths: `<path d="M3 3 L11 11 M11 3 L3 11" stroke="${c}" stroke-width="2" stroke-linecap="round"/>`,
      }
    case 5:
      return {
        vb: '0 0 20 20',
        paths: `<path d="M9.99 0C4.47 0 0 4.48 0 10C0 15.52 4.47 20 9.99 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 9.99 0ZM10 18C5.58 18 2 14.42 2 10C2 5.58 5.58 2 10 2C14.42 2 18 5.58 18 10C18 14.42 14.42 18 10 18ZM10 12C7.67 12 5.68 13.45 4.88 15.5H6.55C7.24 14.31 8.52 13.5 10 13.5C11.48 13.5 12.75 14.31 13.45 15.5H15.12C14.32 13.45 12.33 12 10 12Z" fill="${c}"/><path d="M6.5 9C7.32843 9 8 8.32843 8 7.5C8 6.67157 7.32843 6 6.5 6C5.67157 6 5 6.67157 5 7.5C5 8.32843 5.67157 9 6.5 9Z" fill="${c}"/><path d="M13.5 9C14.3284 9 15 8.32843 15 7.5C15 6.67157 14.3284 6 13.5 6C12.6716 6 12 6.67157 12 7.5C12 8.32843 12.6716 9 13.5 9Z" fill="${c}"/>`,
      }
    case 6:
      return {
        vb: '0 0 20 20',
        paths: `<path d="M9.99 0C4.47 0 0 4.48 0 10C0 15.52 4.47 20 9.99 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 9.99 0ZM10 18C5.58 18 2 14.42 2 10C2 5.58 5.58 2 10 2C14.42 2 18 5.58 18 10C18 14.42 14.42 18 10 18Z" fill="${c}"/><path d="M6.5 9C7.32843 9 8 8.32843 8 7.5C8 6.67157 7.32843 6 6.5 6C5.67157 6 5 6.67157 5 7.5C5 8.32843 5.67157 9 6.5 9Z" fill="${c}"/><path d="M13.5 9C14.3284 9 15 8.32843 15 7.5C15 6.67157 14.3284 6 13.5 6C12.6716 6 12 6.67157 12 7.5C12 8.32843 12.6716 9 13.5 9Z" fill="${c}"/><path d="M7 13.5H13V14.5H7V13.5Z" fill="${c}"/>`,
      }
    case 7:
      return {
        vb: '0 0 20 20',
        paths: `<path d="M9.99 0C4.47 0 0 4.48 0 10C0 15.52 4.47 20 9.99 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 9.99 0ZM10 18C5.58 18 2 14.42 2 10C2 5.58 5.58 2 10 2C14.42 2 18 5.58 18 10C18 14.42 14.42 18 10 18ZM10 14C9.27 14 8.62 13.82 8.04 13.48C7.92 13.62 7.18 14.46 7.03 14.63C7.89 15.18 8.9 15.5 10 15.5C11.11 15.5 12.12 15.17 12.98 14.62C12.01 13.53 12.97 14.6 11.97 13.47C11.38 13.82 10.73 14 10 14Z" fill="${c}"/><path d="M6.5 9C7.32843 9 8 8.32843 8 7.5C8 6.67157 7.32843 6 6.5 6C5.67157 6 5 6.67157 5 7.5C5 8.32843 5.67157 9 6.5 9Z" fill="${c}"/><path d="M13.5 9C14.3284 9 15 8.32843 15 7.5C15 6.67157 14.3284 6 13.5 6C12.6716 6 12 6.67157 12 7.5C12 8.32843 12.6716 9 13.5 9Z" fill="${c}"/>`,
      }
    case 8:
      return {
        vb: '0 0 20 19',
        paths: `<path d="M15.34 11.86C14.47 10.84 13.74 9.97 12.86 8.95C12.4 8.41 11.81 7.87 11.11 7.63C11 7.59 10.89 7.56 10.78 7.54C10.53 7.5 10.26 7.5 9.99999 7.5C9.73999 7.5 9.46999 7.5 9.20999 7.55C9.09999 7.57 8.98999 7.6 8.87999 7.64C8.17999 7.88 7.59999 8.42 7.12999 8.96C6.25999 9.98 5.52999 10.85 4.64999 11.87C3.33999 13.18 1.72999 14.63 2.02999 16.66C2.31999 17.68 3.04999 18.69 4.35999 18.98C5.08999 19.13 7.41999 18.54 9.89999 18.54H10.08C12.56 18.54 14.89 19.12 15.62 18.98C16.93 18.69 17.66 17.67 17.95 16.66C18.26 14.62 16.65 13.17 15.34 11.86Z" fill="${c}"/><path d="M17.5 9C18.8807 9 20 7.88071 20 6.5C20 5.11929 18.8807 4 17.5 4C16.1193 4 15 5.11929 15 6.5C15 7.88071 16.1193 9 17.5 9Z" fill="${c}"/><path d="M13 5C14.3807 5 15.5 3.88071 15.5 2.5C15.5 1.11929 14.3807 0 13 0C11.6193 0 10.5 1.11929 10.5 2.5C10.5 3.88071 11.6193 5 13 5Z" fill="${c}"/><path d="M7 5C8.38071 5 9.5 3.88071 9.5 2.5C9.5 1.11929 8.38071 0 7 0C5.61929 0 4.5 1.11929 4.5 2.5C4.5 3.88071 5.61929 5 7 5Z" fill="${c}"/><path d="M2.5 9C3.88071 9 5 7.88071 5 6.5C5 5.11929 3.88071 4 2.5 4C1.11929 4 0 5.11929 0 6.5C0 7.88071 1.11929 9 2.5 9Z" fill="${c}"/>`,
      }
    case 9:
      return {
        vb: '0 0 24 16',
        paths: `<path d="M19.35 6.04C18.67 2.59 15.64 0 12 0C9.11 0 6.61 1.64 5.36 4.04C2.35 4.36 0 6.9 0 10C0 13.31 2.69 16 6 16H19C21.76 16 24 13.76 24 11C24 8.36 21.95 6.22 19.35 6.04ZM19 14H6C3.79 14 2 12.21 2 10C2 7.79 3.79 6 6 6C8.21 6 10 7.79 10 10H12C12 7.24 10.14 4.92 7.6 4.22C8.61 2.88 10.2 2 12 2C15.03 2 17.5 4.47 17.5 7.5V8H19C20.65 8 22 9.35 22 11C22 12.65 20.65 14 19 14Z" fill="${c}"/>`,
      }
    case 10:
      return {
        vb: '0 0 20 20',
        paths: `<path d="M20 9H15.83L19.07 5.76L17.66 4.34L13 9H11V7L15.66 2.34L14.24 0.93L11 4.17V0H9V4.17L5.76 0.93L4.34 2.34L9 7V9H7L2.34 4.34L0.93 5.76L4.17 9H0V11H4.17L0.93 14.24L2.34 15.66L7 11H9V13L4.34 17.66L5.76 19.07L9 15.83V20H11V15.83L14.24 19.07L15.66 17.66L11 13V11H13L17.66 15.66L19.07 14.24L15.83 11H20V9Z" fill="${c}"/>`,
      }
    case 11:
      return {
        vb: '0 0 10 18',
        paths: `<path d="M5.48 7.9C3.21 7.31 2.48 6.7 2.48 5.75C2.48 4.66 3.49 3.9 5.18 3.9C6.96 3.9 7.62 4.75 7.68 6H9.89C9.82 4.28 8.77 2.7 6.68 2.19V0H3.68V2.16C1.74 2.58 0.18 3.84 0.18 5.77C0.18 8.08 2.09 9.23 4.88 9.9C7.38 10.5 7.88 11.38 7.88 12.31C7.88 13 7.39 14.1 5.18 14.1C3.12 14.1 2.31 13.18 2.2 12H0C0.12 14.19 1.76 15.42 3.68 15.83V18H6.68V15.85C8.63 15.48 10.18 14.35 10.18 12.3C10.18 9.46 7.75 8.49 5.48 7.9Z" fill="${c}"/>`,
      }
    case 12:
      return {
        vb: '-2 0 22 18',
        paths: `<path d="M7.19 3.86415C5.15 6.15415 3.75 9.44414 3.62 9.75414L0 8.20414L4.05 4.15415C4.52 3.68415 5.2 3.47415 5.86 3.60415L7.19 3.86415ZM9.17 14.5141C9.17 14.5141 12.91 12.9641 15.06 10.8141C20.46 5.41414 19.56 1.19415 19.27 0.244145C18.32 -0.0558547 14.1 -0.945855 8.7 4.45415C6.55 6.60415 5 10.3441 5 10.3441L9.17 14.5141ZM15.65 12.3241C13.36 14.3641 10.07 15.7641 9.76 15.8941L11.31 19.5141L15.36 15.4641C15.83 14.9941 16.04 14.3141 15.91 13.6541L15.65 12.3241ZM7 15.5141C7 16.3441 6.66 17.0941 6.12 17.6341C4.94 18.8141 0 19.5141 0 19.5141C0 19.5141 0.7 14.5741 1.88 13.3941C2.42 12.8541 3.17 12.5141 4 12.5141C5.66 12.5141 7 13.8541 7 15.5141ZM11 6.51415C11 5.41415 11.9 4.51415 13 4.51415C14.1 4.51415 15 5.41415 15 6.51415C15 7.61415 14.1 8.51414 13 8.51414C11.9 8.51414 11 7.61415 11 6.51415Z" fill="${c}"/>`,
      }
    case 13:
      return {
        vb: '-2 1 22 18',
        paths: `<path d="M11.11 3.72L10.54 6.61C10.42 7.2 10.58 7.81 10.96 8.27C11.34 8.73 11.9 9 12.5 9H18V10.08L15.43 16H7.34C7.16 16 7 15.84 7 15.66V7.82L11.11 3.72ZM12 0L5.59 6.41C5.21 6.79 5 7.3 5 7.83V15.66C5 16.95 6.05 18 7.34 18H15.44C16.15 18 16.8 17.63 17.16 17.03L19.83 10.88C19.94 10.63 20 10.36 20 10.08V9C20 7.9 19.1 7 18 7H12.5L13.42 2.35C13.47 2.13 13.44 1.89 13.34 1.69C13.11 1.24 12.82 0.83 12.46 0.47L12 0ZM2 7H0V18H2C2.55 18 3 17.55 3 17V8C3 7.45 2.55 7 2 7Z" fill="${c}"/>`,
      }
    case 14:
      return {
        vb: '0 -1.5 20 18',
        paths: `<path d="M8.89 14.28L9.46 11.39C9.58 10.8 9.42 10.19 9.04 9.73C8.66 9.27 8.1 9 7.5 9H2V7.92L4.57 2H12.66C12.84 2 13 2.16 13 2.34V10.18L8.89 14.28ZM8 18L14.41 11.59C14.79 11.21 15 10.7 15 10.17V2.34C15 1.05 13.95 0 12.66 0H4.56C3.85 0 3.2 0.37 2.84 0.97L0.17 7.12C0.0600001 7.37 0 7.64 0 7.92V9C0 10.1 0.9 11 2 11H7.5L6.58 15.65C6.53 15.87 6.56 16.11 6.66 16.31C6.89 16.76 7.18 17.17 7.54 17.53L8 18ZM18 11H20V0H18C17.45 0 17 0.45 17 1V10C17 10.55 17.45 11 18 11Z" fill="${c}"/>`,
      }
    case 15:
      return {
        vb: '0 -1 18 14',
        paths: `<path d="M5.59 10.58L1.42 6.41L0 7.82L5.59 13.41L17.59 1.41L16.18 0L5.59 10.58Z" fill="${c}"/>`,
      }
    case 16:
      return {
        vb: '0 2 22 19',
        paths: `<path d="M12 8H10V13H12V8Z" fill="${c}"/><path d="M12 14H10V16H12V14Z" fill="${c}"/><path d="M11 3.99L18.53 17H3.47L11 3.99ZM11 0L0 19H22L11 0Z" fill="${c}"/>`,
      }
    case 17:
      return {
        vb: '-2 0 22 18',
        paths: `<path d="M16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM11 14H9V6H7V4H11V14Z" fill="${c}"/>`,
      }
    case 18:
      return {
        vb: '-2 0 22 18',
        paths: `<path d="M16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM12 8C12 9.11 11.1 10 10 10H8V12H12V14H6V10C6 8.89 6.9 8 8 8H10V6H6V4H10C11.1 4 12 4.89 12 6V8Z" fill="${c}"/>`,
      }
    case 19:
      return {
        vb: '-2 0 22 18',
        paths: `<path d="M16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM12 7.5C12 8.33 11.33 9 10.5 9C11.33 9 12 9.67 12 10.5V12C12 13.11 11.1 14 10 14H6V12H10V10H8V8H10V6H6V4H10C11.1 4 12 4.89 12 6V7.5Z" fill="${c}"/>`,
      }
    case 20:
      return {
        vb: '-2 0 22 18',
        paths: `<path d="M16 0H2C0.9 0 0 0.9 0 2V16C0 17.1 0.9 18 2 18H16C17.1 18 18 17.1 18 16V2C18 0.9 17.1 0 16 0ZM12 14H10V10H6V4H8V8H10V4H12V14Z" fill="${c}"/>`,
      }
    default:
      return { vb: '0 0 20 20', paths: '' }
  }
}

// ─── Current location pin SVG ─────────────────────────────────────────────────
// Rendered via H.map.DomIcon (not static H.map.Icon) so SVG animations work.
export const CL_W = 44
export const CL_H = 44
export const CL_ANCHOR_X = 22
export const CL_ANCHOR_Y = 22

export function currentLocationSvg(): string {
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${CL_W}" height="${CL_H}" viewBox="0 0 ${CL_W} ${CL_H}">`,
    `<circle cx="22" cy="22" r="11" fill="rgba(10,102,255,0.2)">`,
    `<animate attributeName="r" values="11;20;11" dur="2s" repeatCount="indefinite"/>`,
    `<animate attributeName="opacity" values="1;0;0" dur="2s" repeatCount="indefinite"/>`,
    `</circle>`,
    `<circle cx="22" cy="22" r="8" fill="#3e98ff" stroke="white" stroke-width="3"/>`,
    `</svg>`,
  ].join('')
}

export function knockStatusBadgeSvg(
  color: string,
  icon: number | null,
): string {
  const { vb, paths } = ksIconContent(icon, 'white')
  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${KS_W}" height="${KS_H}" viewBox="0 0 50 50" fill="none">`,
    // Triangle pointer
    `<path d="M20.2539 35.0234H27.8414L27.0826 40.3347L25.5651 42.6109L20.2539 35.0234Z" fill="${color}"/>`,
    // Filled circle
    `<path d="M39.9807 24.5607C39.9807 32.1035 33.5264 39.7356 25.5646 39.7356C17.6028 39.7356 11.1484 32.1035 11.1484 24.5607C11.1484 17.0179 17.6028 10.1445 25.5646 10.1445C33.5264 10.1445 39.9807 17.0179 39.9807 24.5607Z" fill="${color}"/>`,
    // White border — stroke the gold body's outline so the band is a uniform 2px
    // on screen, matching the record pin's white stroke. stroke-width 2.857 in the
    // 50-unit viewBox renders at the 35px badge size (35/50 = 0.7 scale) as
    // 0.7 * 2.857 ≈ 2px. The stroke straddles the outline and paints white over the
    // outer gold edge — the same way the record pin's filled+stroked body does, so
    // the visible white band equals the full stroke width on both pins.
    `<path d="M15.7411 34.7745C13.7191 32.8328 12.3185 30.3348 11.7168 27.5968C11.115 24.8588 11.3391 22.0038 12.3607 19.3932C13.3823 16.7826 15.1554 14.5337 17.4557 12.9313C19.756 11.3289 22.4799 10.445 25.2828 10.3914C28.0856 10.3378 30.8414 11.117 33.2012 12.6303C35.561 14.1436 37.4188 16.323 38.5395 18.8927C39.6601 21.4623 39.9932 24.3067 39.4965 27.0657C38.9998 29.8247 37.6958 32.3744 35.7494 34.3919C33.6355 36.5889 30.8615 38.0347 27.8498 38.5092C27.7457 38.5262 27.6468 38.5666 27.5607 38.6274C27.4745 38.6882 27.4033 38.7679 27.3525 38.8603L25.4097 42.4029L23.2125 38.7321C23.1621 38.6477 23.0943 38.5751 23.0137 38.5189C22.9331 38.4627 22.8415 38.4242 22.7449 38.4061C20.1053 37.9069 17.6701 36.6442 15.7411 34.7745Z" fill="none" stroke="white" stroke-width="2.857" stroke-linejoin="round"/>`,
    // Icon centred in the circle (circle centre ≈ 25.5, 24.5; icon 20×20 → offset 15.5, 14.5)
    `<svg x="15.5" y="14.5" width="20" height="20" viewBox="${vb}">`,
    paths,
    `</svg>`,
    `</svg>`,
  ].join('')
}
